/**
 * React Router loader functions for the AetherProxy dashboard.
 *
 * Loaders execute **before** the route component mounts, so the page
 * renders with data already available via `useLoaderData()`.  This
 * eliminates the "loading flicker" that occurs with `useFetch` or
 * `useResource` (which start fetching only after the component mounts).
 *
 * ## Architecture
 *
 * ┌──────────────┐      loader runs        ┌────────────────────┐
 * │  User clicks  │ ──→  before mount  ──→  │  Component renders │
 * │  /logs link   │     fetches data        │  with data already │
 * └──────────────┘                          │  in hand (no spin) │
 *                                           └────────────────────┘
 *
 * ## Hybrid pattern
 *
 * Loaders handle the **initial** data fetch.  For mutations and refresh,
 * pages still use `useFetch` or `useResource.reload()`.  This gives the
 * best of both worlds:
 *
 * - Zero loading flicker on page navigation
 * - Full interactivity (refresh, filter changes, mutations) after mount
 *
 * ## Auth
 *
 * Loaders use `getAuthToken()` (Clerk via `window.Clerk` or admin-token
 * from localStorage) — they do NOT depend on React context.
 *
 * When no auth token is available, `loaderFetch()` returns `null`.  Loaders
 * propagate that as `null` in the response, and the component falls back
 * to `useFetch` which will retry once auth becomes available.
 *
 * @module loaders
 */

import { loaderFetch } from './loader-api';
import type { ListResponse, ProviderRow, UsageRow, ProviderPreset, ModelRow, VirtualKeyRow, RoutingPolicyRow } from './types';

/* ── Types for loader return values ──────────────────────────── */

/** Data returned by the `/logs` route loader */
export interface LogsLoaderData {
  usage: ListResponse<UsageRow> | null;
  providers: ListResponse<ProviderRow> | null;
  models: ListResponse<ModelRow> | null;
  keys: ListResponse<VirtualKeyRow> | null;
}

/** Data returned by the `/providers` route loader */
export interface ProvidersLoaderData {
  providers: ListResponse<ProviderRow> | null;
  presets: ListResponse<ProviderPreset> | null;
}

/** Data returned by the `/models` route loader */
export interface ModelsLoaderData {
  models: ListResponse<ModelRow> | null;
  providers: ListResponse<ProviderRow> | null;
}

/** Data returned by the `/keys` route loader */
export interface KeysLoaderData {
  keys: ListResponse<VirtualKeyRow> | null;
  providers: ListResponse<ProviderRow> | null;
  models: ListResponse<ModelRow> | null;
}

/** Data returned by the `/routing` route loader */
export interface RoutingLoaderData {
  policies: ListResponse<RoutingPolicyRow> | null;
}

/* ── Loader functions ────────────────────────────────────────── */

/**
 * Logs page loader — fetches usage logs + reference data (providers,
 * models, keys) in parallel.
 *
 * Note: This fetches the default dataset (no search/filter params).
 * Search and filter are client-side operations handled by `useFetch`
 * after the component mounts.
 */
export async function logsLoader(): Promise<LogsLoaderData> {
  const [usage, providers, models, keys] = await Promise.all([
    loaderFetch<ListResponse<UsageRow>>('/admin/usage?hours=168&limit=500'),
    loaderFetch<ListResponse<ProviderRow>>('/admin/providers'),
    loaderFetch<ListResponse<ModelRow>>('/admin/models'),
    loaderFetch<ListResponse<VirtualKeyRow>>('/admin/keys'),
  ]);

  return { usage, providers, models, keys };
}

/**
 * Providers page loader — fetches providers and presets in parallel.
 */
export async function providersLoader(): Promise<ProvidersLoaderData> {
  const [providers, presets] = await Promise.all([
    loaderFetch<ListResponse<ProviderRow>>('/admin/providers'),
    loaderFetch<ListResponse<ProviderPreset>>('/admin/providers/presets'),
  ]);

  return { providers, presets };
}

/**
 * Models page loader — fetches models and providers in parallel.
 */
export async function modelsLoader(): Promise<ModelsLoaderData> {
  const [models, providers] = await Promise.all([
    loaderFetch<ListResponse<ModelRow>>('/admin/models'),
    loaderFetch<ListResponse<ProviderRow>>('/admin/providers'),
  ]);

  return { models, providers };
}

/**
 * Keys page loader — fetches keys, providers, and models in parallel.
 */
export async function keysLoader(): Promise<KeysLoaderData> {
  const [keys, providers, models] = await Promise.all([
    loaderFetch<ListResponse<VirtualKeyRow>>('/admin/keys'),
    loaderFetch<ListResponse<ProviderRow>>('/admin/providers'),
    loaderFetch<ListResponse<ModelRow>>('/admin/models'),
  ]);

  return { keys, providers, models };
}

/**
 * Routing page loader — fetches routing policies.
 */
export async function routingLoader(): Promise<RoutingLoaderData> {
  const policies = await loaderFetch<ListResponse<RoutingPolicyRow>>('/admin/routing-policies');
  return { policies };
}
